import { ADD_TO_CART, REMOVE_FROM_CART } from "redux/types";

// Define types for the project and cart state
interface Project {
  id: number;
  amount: number;
  duration?: number;
}

interface CartState {
  totalItems: number;
  totalPrice: number;
  data: {
    humanitarian_projects: Project[];
    internal_projects: Project[];
    seasonal_projects: Project[];
    campaign_projects: Project[];
    external_projects: Project[];
  };
}

const initialState: { cart: CartState } = {
  cart: {
    totalItems: 0,
    totalPrice: 0,
    data: {
      humanitarian_projects: [],
      internal_projects: [],
      seasonal_projects: [],
      campaign_projects: [],
      external_projects: [],
    },
  },
};

// Define action types
interface AddToCartAction {
  type: typeof ADD_TO_CART;
  payload: {
    category: keyof CartState["data"];
    project: Project;
  };
}

interface RemoveFromCartAction {
  type: typeof REMOVE_FROM_CART;
  payload: {
    category: keyof CartState["data"];
    projectId: number;
  };
}

type CartActionTypes = AddToCartAction | RemoveFromCartAction;

export const guestCartReducer = (
  state = initialState,
  action: CartActionTypes,
): { cart: CartState } => {
  switch (action.type) {
    /////////////////////// case adding items
    case ADD_TO_CART: {
      const { category, project } = action.payload;
      const existingProjectIndex = state.cart.data[category].findIndex(
        (p) => p.id === project.id,
      );

      if (existingProjectIndex !== -1) {
        // If project already exists, update it
        const newCategoryProjects = state.cart.data[category].map((p, index) =>
          index === existingProjectIndex ? project : p,
        );
        const oldProject = state.cart.data[category][existingProjectIndex];
        const newTotalPrice =
          state.cart.totalPrice - oldProject.amount + project.amount;

        return {
          ...state,
          cart: {
            ...state.cart,
            totalPrice: newTotalPrice,
            data: {
              ...state.cart.data,
              [category]: newCategoryProjects,
            },
          },
        };
      } else {
        // If project does not exist, add it
        const newCategoryProjects = [...state.cart.data[category], project];
        const newTotalItems = state.cart.totalItems + 1;
        const newTotalPrice = state.cart.totalPrice + project.amount;

        return {
          ...state,
          cart: {
            ...state.cart,
            totalItems: newTotalItems,
            totalPrice: newTotalPrice,
            data: {
              ...state.cart.data,
              [category]: newCategoryProjects,
            },
          },
        };
      }
    }

    /////////////////////// case removing items
    case REMOVE_FROM_CART: {
      const { category, projectId } = action.payload;
      const newCategoryProjects = state.cart.data[category].filter(
        (project) => project.id !== projectId,
      );
      const removedProject = state.cart.data[category].find(
        (project) => project.id === projectId,
      );
      const newTotalItems = state.cart.totalItems - 1;
      const newTotalPrice = removedProject
        ? state.cart.totalPrice - removedProject.amount
        : state.cart.totalPrice;

      return {
        ...state,
        cart: {
          ...state.cart,
          totalItems: newTotalItems,
          totalPrice: newTotalPrice,
          data: {
            ...state.cart.data,
            [category]: newCategoryProjects,
          },
        },
      };
    }

    default:
      return state;
  }
};
